import { ExportIcon } from "@xola/icons";
import { Alert, ButtonGroup, Tooltip } from "@xola/ui-kit";
import { DateFormat, formatDate, isFuture, JsDateRange, now } from "common";
import React, { useState } from "react";
import { ExportJob, Permission, Product, ProductObject, PurchaseItemObject, Seller } from "types";
import shallow from "zustand/shallow";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useResource } from "../../../hooks/useResource";
import { useModal } from "../../../hooks/useVisibility";
import { getInitialDate, getNewTransactionParams } from "../../../models/Reports";
import {
    AnalyticsMode,
    AnalyticsView,
    DateFilterRange,
    useAnalyticsFilterStore,
} from "../../../stores/reports/analyticsFilterStore";
import { ExportJobModal } from "../../Common/ExportJobModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ListFilter } from "../../Dashboard/Filters/ListFilter";
import { ItemSummary } from "../AnalyticsProductPicker";
import { ExportTransactionsModal } from "../ExportTransactionsModal";
import { ReportDatePicker } from "../ReportDatePicker";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

// Signature is defined by ListFilter.fetch (seller, collection, params).
// We only need params in this implementation.
const useFetchProducts = (_seller: Seller, _collection: string[], params: any) => {
    return useAutoFetchCollection<Product>("products", {
        ...params,
        "object[in]": [ProductObject.EXPERIENCE, ProductObject.WARE].join(","),
    });
};

export const AnalyticsFilter = () => {
    const { isGranted } = useAuthorization();
    const [from, to] = useAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [setFrom, setTo] = useAnalyticsFilterStore((state) => [state.setFrom, state.setTo], shallow);
    const [filters, setFilters] = useAnalyticsFilterStore((state) => [state.filters, state.setFilters], shallow);
    const isCashFlow = filters.mode === AnalyticsMode.CASH_FLOW;
    const showMerchandise = isGranted(Permission.MERCHANDISE_VIEW);

    const setMode = useAnalyticsFilterStore((state) => state.setMode);
    const dateParams = useAnalyticsFilterStore((state) => state.getDateParams());

    const exportEventsModal = useModal(false);
    const exportTransactionsModal = useModal(false);
    const [value, setValue] = useState<JsDateRange>(getInitialDate(from, to));

    const activeMode = filters.mode === AnalyticsMode.RECOGNIZED ? 0 : 1;
    const activeView = filters.view === AnalyticsView.CHART ? 0 : 1;

    const handleModeChange = (activeIndex: number) => {
        const mode = activeIndex === 0 ? AnalyticsMode.RECOGNIZED : AnalyticsMode.CASH_FLOW;
        setMode(mode);
    };

    const handleViewChange = (activeIndex: number) => {
        const view = activeIndex === 0 ? AnalyticsView.CHART : AnalyticsView.TABLE;
        setFilters({ view });
    };

    const handleDateChange = (dateValue: JsDateRange) => {
        if (dateValue.from && dateValue.to) {
            setFrom(now(dateValue.from));
            setTo(now(dateValue.to));
        }

        setValue(dateValue);
    };

    const params = getNewTransactionParams(dateParams, { filters, format: "xlsx" });

    return (
        <div className="flex w-full justify-between border-b border-gray-light px-6 py-3 align-top">
            <span className="w-3/5 flex-grow truncate align-top">
                <div className="flex flex-wrap items-center gap-2">
                    <ReportDatePicker
                        to={to}
                        from={from}
                        value={value}
                        max={isCashFlow ? now() : null}
                        disabledDays={(date) => isCashFlow && isFuture(date, "day")}
                        isFutureDatesAllowed={filters.mode === AnalyticsMode.RECOGNIZED}
                        onChange={handleDateChange}
                    />
                    <ListFilter
                        label="Product"
                        title="Filter Products"
                        fetch={showMerchandise ? useFetchProducts : undefined}
                        collection={["experiences"]}
                        target={ItemSummary}
                        infoComponent={InfoComponent}
                        value={filters.products}
                        onChange={(products) => setFilters({ products })}
                    />
                    <ButtonGroup className="align-top" size="medium" value={activeMode} onChange={handleModeChange}>
                        <ButtonGroup.Button>
                            <Tooltip
                                placement="bottom"
                                content={
                                    <span>
                                        Recognized Earnings shows data based on the arrival date of the booking. For
                                        transactions that occur on or before the arrival date of the booking, the
                                        transaction is recognized on the arrival date. For transactions that occur after
                                        the arrival date, they are recognized at the time of the transaction.
                                    </span>
                                }
                            >
                                Recognized Earnings
                            </Tooltip>
                        </ButtonGroup.Button>

                        <ButtonGroup.Button>
                            <Tooltip
                                placement="bottom"
                                content={
                                    <span>
                                        Cash flow shows data based on the date money was received regardless of the date
                                        of arrival.
                                    </span>
                                }
                            >
                                Cash Flow
                            </Tooltip>
                        </ButtonGroup.Button>
                    </ButtonGroup>
                    <ButtonGroup className="align-top" size="medium" value={activeView} onChange={handleViewChange}>
                        <ButtonGroup.Button>Chart</ButtonGroup.Button>
                        <ButtonGroup.Button>Table</ButtonGroup.Button>
                    </ButtonGroup>
                </div>
            </span>
            <span className="w-2/5 flex-grow truncate align-top">
                <div className="flex flex-wrap justify-end gap-2">
                    <SecondaryButton icon={<ExportIcon />} onClick={exportEventsModal.show}>
                        Export Events
                    </SecondaryButton>
                    <SecondaryButton icon={<ExportIcon />} onClick={exportTransactionsModal.show}>
                        Export Transactions
                    </SecondaryButton>
                </div>
            </span>
            <ExportEventsModal isOpen={exportEventsModal.isVisible} onClose={exportEventsModal.hide} />
            <ExportTransactionsModal
                params={params}
                isOpen={exportTransactionsModal.isVisible}
                onClose={exportTransactionsModal.hide}
            />
        </div>
    );
};

const ExportEventsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: (e: any) => void }) => {
    const filters = useAnalyticsFilterStore((state) => state.filters);
    const { mode } = filters;
    const from = useAnalyticsFilterStore((state) => state.getFrom());
    const to = useAnalyticsFilterStore((state) => state.getTo());
    const name = mode === AnalyticsMode.RECOGNIZED ? DateFilterRange.ARRIVAL_DATE : DateFilterRange.CREATED_AT;
    const dateParams = [formatDate(from, DateFormat.DATE_TIME_ISO), formatDate(to, DateFormat.DATE_TIME_ISO)];

    const params: any = {
        cover: false,
        [name]: dateParams.join(","),
        "object[in]": [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.ORDER].join(","),
    };
    if (filters.products.selected.length > 0) {
        params["product[in]"] = filters.products.selected.join(",");
    }

    const { data: job } = useResource<ExportJob>(["analytics", "export", "trips"], params, {
        enabled: isOpen,
        staleTime: 1000,
    });
    return <ExportJobModal isOpen={isOpen} job={job} onClose={onClose} />;
};

const InfoComponent = () => (
    <Alert color="warning">
        <span>
            Gifts and store credit transactions will not be included in your report if{" "}
            <strong className="font-bold">&quot;Select All&quot;</strong> listings is not checked. Gift and store credit
            data can be found in Reports &gt; Gifts/Store credits
        </span>
    </Alert>
);
